import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Checkbox, Input, Radio, Skeleton } from '@dreamtree-org/twreact-ui';
import { Lock, Plus, Smartphone, Truck, Zap } from 'lucide-react';
import { EmptyState, Money, SectionCard } from '@/components';
import { useMutation, useQuery } from '@/lib/useQuery';
import { errorText, formatDate } from '@/lib/format';
import { cartTotals, qtyOf } from '../lib/cart';
import { lineKey } from '../lib/variants';
import { consentLabel } from '../lib/terms';
import { useStorefront } from '../StorefrontContext';
import CartLineItem from '../components/CartLineItem';
import CheckoutSteps from '../components/CheckoutSteps';
import PriceSummary from '../components/PriceSummary';
import AddressForm from '../components/AddressForm';
import PhoneOtpForm, { useSignInMethod } from '../components/PhoneOtpForm';
import { track } from '../lib/beacon';

const PAYMENT_METHODS = [
  { value: 'upi', label: 'UPI', hint: 'GPay, PhonePe, Paytm or any UPI app' },
  { value: 'card', label: 'Credit / debit card', hint: 'Visa, Mastercard, RuPay' },
  { value: 'netbanking', label: 'Net banking', hint: 'All major Indian banks' },
];

const radio = 'var(--color-primary-600)';
const time = (iso) => new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

/**
 * WEB-05 checkout (mockup 04, split screen): address + PIN eligibility, delivery
 * speed, coupon, price/tax/shipping breakdown from a server quote with a TTL,
 * payment method, policy consent and retry-safe order placement (idempotency key).
 * Payment goes through the server's payment adapter (built-in test provider: no money moves).
 * A guest verifies their phone inline first (MERCH-56): the buyer is still an
 * identified customer — Checkout.quote and SalesOrder.place stay customer-only.
 */
export default function Checkout() {
  useEffect(() => track('checkout_start'), []);
  const sf = useStorefront();
  if (!sf.sessionReady) return <Skeleton count={6} />;
  if (!sf.session) return <PhoneStep />;
  return <CheckoutForm />;
}

/** { status, code, message } of a failed order placement. */
const failureOf = (ex) => {
  const res = ex && ex.response;
  return { status: (res && res.status) || null, code: (res && res.data && res.data.code) || null, message: errorText(ex) };
};

/**
 * What the shopper can do after placement failed: a declined payment is retried on
 * the same order (same idempotency key, method may change); a lost hold or stale
 * quote (409) re-quotes and points back to the cart; anything else is shown as is.
 */
export function PlaceFailure({ failure, onRetry, retrying }) {
  if (failure.status === 402) {
    return (
      <Alert variant="error" title="Payment not completed">
        <div className="space-y-2">
          <div>{failure.message}</div>
          <Button size="sm" variant="outline" loading={retrying} onClick={onRetry}>Retry payment</Button>
        </div>
      </Alert>
    );
  }
  if (failure.status === 409) {
    return (
      <Alert variant="warning" title="Your order changed while you were checking out">
        <div className="space-y-2">
          <div>{failure.message}</div>
          <Link to="/cart" className="inline-flex min-h-11 items-center text-sm font-medium text-primary-700 hover:underline">Back to cart</Link>
        </div>
      </Alert>
    );
  }
  return <Alert variant="error" title="Order not placed">{failure.message}</Alert>;
}

/** Step 1 for a guest: verify the phone inline, then the checkout continues on this page. */
function PhoneStep() {
  const sf = useStorefront();
  const navigate = useNavigate();
  const method = useSignInMethod();
  const items = sf.cart.items;
  const localTotals = useMemo(() => cartTotals(items), [items]);
  if (!items.length) {
    return (
      <div className="space-y-4">
        <CheckoutSteps current={1} />
        <EmptyState title="Nothing to check out" action={<Button onClick={() => navigate('/shop')}>Browse the shop</Button>}>Your cart is empty.</EmptyState>
      </div>
    );
  }
  const verified = async (session) => {
    sf.setSession(session);
    // The server merged the guest cart into the customer's at sign-in.
    await sf.refreshCart();
  };
  return (
    <div className="space-y-4">
      <CheckoutSteps current={1} />
      <div className="grid gap-5 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-7">
          <SectionCard title="Your phone">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600"><Smartphone className="h-5 w-5" aria-hidden="true" /></span>
              <p className="text-sm opacity-80">Verify your mobile number to place the order. We use it for delivery updates and to find your orders later. No password needed.</p>
            </div>
            <PhoneOtpForm method={method} onVerified={verified} submitLabel="Verify and continue to address" />
          </SectionCard>
        </div>
        <div className="space-y-4 lg:col-span-5">
          <SectionCard title={`Order summary (${items.reduce((n, i) => n + qtyOf(i), 0)})`}>
            <div className="divide-y divide-[var(--app-border)]">
              {items.map((i) => <CartLineItem key={lineKey(i.listing_id, i.variant_id)} item={i} compact />)}
            </div>
            <PriceSummary t={localTotals} />
            <p className="text-xs opacity-70">Delivery, coupons and the final price are confirmed after you verify your phone.</p>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

function CheckoutForm() {
  const sf = useStorefront();
  const navigate = useNavigate();
  const account = useQuery('sfAccount', {}, { select: (r) => r.data });
  const seller = useQuery('sfSeller', { slug: sf.seller.slug, name: sf.seller.name }, { select: (r) => r.data });
  const consentText = consentLabel(seller.data);
  const addresses = (account.data && account.data.addresses) || [];
  const [addressId, setAddressId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [shipping, setShipping] = useState('standard');
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState('');
  const [method, setMethod] = useState('upi');
  const [upi, setUpi] = useState('');
  const [consent, setConsent] = useState(false);
  const [placeError, setPlaceError] = useState(null);
  const idem = useRef(`ck-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
  const couponCheck = useMutation('sfCouponApply');
  const place = useMutation('sfOrderPlace');

  useEffect(() => {
    if (!addressId && addresses.length) setAddressId((addresses.find((a) => a.is_default) || addresses[0]).id);
    if (account.data && !addresses.length) setAdding(true);
  }, [account.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const address = addresses.find((a) => a.id === addressId) || null;
  const items = sf.cart.items;
  // Re-quote when the address PIN, shipping, coupon or cart contents change (the server holds the units).
  const quote = useQuery('sfCheckoutQuote', { pin: address ? address.pin : '', shipping, coupon, lines: items.map((i) => `${lineKey(i.listing_id, i.variant_id)}x${qtyOf(i)}`).join(',') }, { select: (r) => r.data, enabled: items.length > 0 });
  const testPayments = true; // the built-in test provider is the only payment provider until a real gateway is configured
  const q = quote.data;
  const localTotals = useMemo(() => cartTotals(items), [items]);
  const delivery = q && q.delivery;
  const unserviceable = quote.error && /deliver/i.test(quote.error);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    try {
      const r = await couponCheck.run({ code: couponInput, subtotal_paise: localTotals.subtotal_paise });
      setCoupon(r.data.code);
    } catch {
      setCoupon('');
    }
  };

  const pay = async () => {
    setPlaceError(null);
    try {
      const r = await place.run({
        idempotency_key: idem.current,
        quote: q,
        address,
        payment_method: method,
        upi_id: method === 'upi' ? upi : undefined,
        consent,
        seller: sf.seller,
      });
      await sf.refreshCart();
      navigate(`/checkout/confirmed/${r.data.id}`, { replace: true });
    } catch (ex) {
      const f = failureOf(ex);
      setPlaceError(f);
      if (f.status === 409) quote.reload();
    }
  };

  if (!items.length) {
    return (
      <div className="space-y-4">
        <CheckoutSteps current={1} />
        <EmptyState title="Nothing to check out" action={<Button onClick={() => navigate('/shop')}>Browse the shop</Button>}>Your cart is empty.</EmptyState>
      </div>
    );
  }

  const canPay = !!address && !!q && !quote.error && !!consentText && consent && !unserviceable && !place.loading;

  return (
    <div className="space-y-4">
      <CheckoutSteps current={address ? 2 : 1} />

      <div className="grid gap-5 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-7">
          <SectionCard
            title="Delivery address"
            actions={!adding ? <Button size="sm" variant="ghost" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>Add new</Button> : null}
          >
            {account.loading && !account.data ? <Skeleton count={3} /> : null}
            {account.error ? <Alert variant="error">{account.error}</Alert> : null}
            <div className="space-y-2" role="radiogroup" aria-label="Delivery address">
              {addresses.map((a) => (
                <div key={a.id} className={`rounded-lg border p-3 ${a.id === addressId ? 'border-primary-600 bg-primary-50' : 'border-[var(--app-border)]'}`}>
                  <Radio
                    name="address"
                    value={a.id}
                    checked={a.id === addressId}
                    onChange={() => setAddressId(a.id)}
                    radioColor={radio}
                    label={
                      <span className="block text-sm leading-snug">
                        <span className="font-semibold">{a.name}</span> <Badge size="xs" variant="outline">{a.label}</Badge>
                        <span className="block opacity-80">{[a.line1, a.line2, a.city].filter(Boolean).join(', ')} {a.pin}</span>
                        {a.phone ? <span className="block text-xs opacity-60">+91 {a.phone}</span> : null}
                      </span>
                    }
                  />
                </div>
              ))}
            </div>
            {adding ? (
              <div className="rounded-lg border border-dashed border-[var(--app-border)] p-3">
                <AddressForm
                  initial={{ name: (sf.session && sf.session.name) || '', phone: (sf.session && sf.session.phone) || '' }}
                  onCancel={addresses.length ? () => setAdding(false) : undefined}
                  onSaved={async (a) => {
                    await account.reload();
                    setAddressId(a.id);
                    setAdding(false);
                  }}
                />
              </div>
            ) : null}
            {unserviceable ? <Alert variant="warning" title="We can't deliver here yet">{quote.error}</Alert> : null}
            {quote.error && !unserviceable ? (
              <Alert variant="error" title="We couldn't hold your items">
                <div className="space-y-2">
                  <div>{quote.error}</div>
                  <Button size="sm" variant="outline" onClick={() => navigate('/cart')}>Review cart</Button>
                </div>
              </Alert>
            ) : null}
          </SectionCard>

          <SectionCard title="Delivery speed">
            {!address ? <p className="text-sm opacity-70">Choose an address to see delivery dates for its PIN code.</p> : null}
            {address && !delivery && !quote.error ? <Skeleton count={2} /> : null}
            {delivery ? (
              <div className="space-y-2">
                <ShipOption value="standard" current={shipping} onChange={setShipping} icon={Truck} title="Standard delivery" sub={`By ${formatDate(delivery.standard_eta_date)} · ${delivery.city} ${delivery.pin}`} price="Free" />
                <ShipOption value="express" current={shipping} onChange={setShipping} icon={Zap} title="Express delivery" sub={delivery.express_available ? `Next day · by ${formatDate(delivery.express_eta_date)}` : 'Not available for this PIN code'} price={<Money paise={delivery.express_paise} />} disabled={!delivery.express_available} />
              </div>
            ) : null}
          </SectionCard>

          <SectionCard title="Payment method">
            <div className="space-y-2" role="radiogroup" aria-label="Payment method">
              {PAYMENT_METHODS.map((m) => (
                <div key={m.value} className={`rounded-lg border p-3 ${method === m.value ? 'border-primary-600 bg-primary-50' : 'border-[var(--app-border)]'}`}>
                  <Radio name="payment" value={m.value} checked={method === m.value} onChange={() => setMethod(m.value)} radioColor={radio} label={<span className="text-sm"><span className="font-semibold">{m.label}</span> <span className="opacity-70">· {m.hint}</span></span>} />
                  {method === 'upi' && m.value === 'upi' ? (
                    <div className="mt-3 max-w-sm"><Input label="UPI ID (optional)" placeholder="name@okhdfcbank" value={upi} onChange={(e) => setUpi(e.target.value)} helperText="Leave blank to pay by scanning a QR on the next screen." /></div>
                  ) : null}
                  {method === m.value && m.value !== 'upi' ? <p className="mt-2 text-xs opacity-70">You will enter details on the secure payment page.</p> : null}
                </div>
              ))}
            </div>
            {testPayments ? (
              <p className="rounded-md border border-dashed border-[var(--app-border)] p-3 text-xs opacity-80">
                Test payments: no money moves. Use UPI ID <span className="font-mono">fail@test</span> to see a declined payment and retry.
              </p>
            ) : null}
          </SectionCard>
        </div>

        <div className="space-y-4 lg:col-span-5">
          <div className="space-y-4 lg:sticky lg:top-36">
            <SectionCard title={`Order summary (${items.reduce((n, i) => n + qtyOf(i), 0)})`}>
              <p className="text-xs opacity-70">Sold by {(sf.cart.seller && sf.cart.seller.name) || sf.seller.name}</p>
              <div className="divide-y divide-[var(--app-border)]">
                {items.map((i) => <CartLineItem key={lineKey(i.listing_id, i.variant_id)} item={i} compact />)}
              </div>
              <div className="flex items-start gap-2">
                <div className="flex-1"><Input aria-label="Coupon code" placeholder="Coupon code (try WELCOME500)" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} /></div>
                <Button variant="outline" loading={couponCheck.loading} onClick={applyCoupon}>Apply</Button>
              </div>
              {couponCheck.error ? <Alert variant="error">{couponCheck.error}</Alert> : null}
              {coupon && !couponCheck.error ? (
                <Alert variant="success" dismissible onDismiss={() => { setCoupon(''); setCouponInput(''); }}>{coupon} applied: {q && q.coupon ? q.coupon.label : 'discount'}</Alert>
              ) : null}
              {q ? <PriceSummary t={q.totals} couponCode={coupon} shippingLabel={shipping === 'express' ? 'Express delivery' : 'Delivery'} /> : <Skeleton count={5} />}
              {q ? <p className="text-xs opacity-70">Price and stock held until {time(q.expires_at)}.</p> : null}
            </SectionCard>

            <SectionCard>
              {consentText ? (
                <Checkbox
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  label={consentText}
                />
              ) : seller.error ? (
                <Alert variant="error">The store's return and warranty terms could not be loaded. Refresh to try again.</Alert>
              ) : (
                <Skeleton count={2} />
              )}
              {placeError ? (
                <PlaceFailure failure={placeError} onRetry={pay} retrying={place.loading} />
              ) : null}
              <Button size="lg" fullWidth disabled={!canPay} loading={place.loading} leftIcon={<Lock className="h-4 w-4" />} onClick={pay}>
                {q ? <>Pay&nbsp;<Money paise={q.totals.total_paise} /></> : 'Pay'}
              </Button>
              {!consent ? <p className="text-xs opacity-70">Tick the policy box to enable payment.</p> : null}
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShipOption({ value, current, onChange, icon: Icon, title, sub, price, disabled }) {
  return (
    <div className={`flex items-center gap-3 rounded-lg border p-3 ${current === value ? 'border-primary-600 bg-primary-50' : 'border-[var(--app-border)]'} ${disabled ? 'opacity-60' : ''}`}>
      <Radio name="shipping" value={value} checked={current === value} disabled={disabled} onChange={() => onChange(value)} radioColor={radio} ariaLabel={title} />
      <Icon className="h-5 w-5 shrink-0 text-primary-600" aria-hidden="true" />
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-semibold">{title}</div>
        <div className="text-xs opacity-70">{sub}</div>
      </div>
      <div className="text-sm font-semibold">{price}</div>
    </div>
  );
}
